import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useProject } from "../../layout/ProjectLayout";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Sparkles } from "lucide-react";

function ListBlock({ title, items }) {
  return (
    <div className="border border-border bg-card p-4">
      <div className="overline-label mb-2">{title}</div>
      {(!items || items.length === 0) ? <div className="text-sm text-muted-foreground">—</div> : (
        <ul className="space-y-1.5 text-sm list-none">
          {items.map((it, i) => <li key={i} className="pl-3 border-l-2 border-[#1E6FE8]">{it}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function Charter() {
  const { projectId } = useParams();
  const { project, reloadProject } = useProject();
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  if (!project) return <div className="text-muted-foreground">Loading…</div>;

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/generate`, { generationType: "charter" });
      setDraft(data.result);
      toast.success("AI charter draft generated — review and apply");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const apply = async () => {
    try {
      await api.patch(`/projects/${projectId}`, {
        purposeBusinessCase: draft.purposeBusinessCase,
        objectives: draft.objectives, successCriteria: draft.successCriteria,
        inScope: draft.inScope, outOfScope: draft.outOfScope,
        summaryMilestones: (draft.summaryMilestones || []).map((m) => ({ ...m, status: "NotStarted" })),
        assumptionsConstraints: draft.assumptionsConstraints,
        highLevelRisks: draft.highLevelRisks,
      });
      setDraft(null);
      reloadProject();
      toast.success("Charter updated from AI draft");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const src = draft || project;

  return (
    <div data-testid="charter-page">
      <PageHeader overline="PMBOK · Initiation" title="Project Charter"
        actions={
          <>
            {draft && <Button data-testid="apply-charter-btn" onClick={apply} className="rounded-none bg-[#00C781] hover:bg-[#00a86b] text-black">Apply AI draft</Button>}
            <Button data-testid="generate-charter-btn" onClick={generate} disabled={busy} variant="outline" className="rounded-none border-[#1E6FE8] text-[#1E6FE8]">
              <Sparkles className="h-4 w-4 mr-1" />{busy ? "Generating…" : "Generate with AI"}
            </Button>
          </>
        }>
        {draft && <div className="text-xs text-amber-600 mt-1">Previewing AI draft — not yet saved</div>}
      </PageHeader>

      <div className="border border-border bg-card p-4 mb-4">
        <div className="overline-label mb-2">Purpose / Business Case</div>
        <p className="text-sm leading-relaxed max-w-3xl">{src.purposeBusinessCase || "—"}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <ListBlock title="Objectives" items={src.objectives} />
        <ListBlock title="Success Criteria" items={src.successCriteria} />
        <ListBlock title="In Scope" items={src.inScope} />
        <ListBlock title="Out of Scope" items={src.outOfScope} />
        <ListBlock title="Assumptions & Constraints" items={src.assumptionsConstraints} />
        <ListBlock title="High-Level Risks" items={src.highLevelRisks} />
      </div>

      <div className="border border-border bg-card">
        <div className="px-4 py-2.5 border-b border-border overline-label">Summary Milestones</div>
        <table className="w-full text-sm data-table">
          <thead><tr><th>Milestone</th><th>Target Date</th><th>Status</th></tr></thead>
          <tbody>
            {(src.summaryMilestones || []).map((m, i) => (
              <tr key={i}><td>{m.name}</td><td>{m.targetDate}</td><td><StatusBadge value={m.status || "Planned"} /></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
